import React from 'react';
import PropTypes from 'prop-types';
import { Box, ButtonBase, Grid, Typography } from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import TextHighlighter from '../../../atoms/TextHighlighter';
import ArticleModalActiveBars from './ArticleModalActiveBars';
import ArticleModalHeader from './ArticleModalHeader';
import ArticleModalLinkedInContent from './ArticleModalLinkedInContent';
import ArticleModalReadingTools from './ArticleModalReadingTools';
import ArticleModalSectionTabs from './ArticleModalSectionTabs';
import ArticleModalSidebar from './ArticleModalSidebar';
import ArticleModalStandardContent from './ArticleModalStandardContent';

const ArticleModalLoadedContent = ({
  actionAnchorEl,
  article,
  briefingKeywords,
  contentContainerRef,
  currentMatch,
  fontSize,
  highlightEnabled,
  highlights,
  isMobile,
  isOpen,
  isPaused,
  isSearchOpen,
  isSpeaking,
  isTTSSupported,
  kpiaAnalyzing,
  progress,
  qualitativeAnalysisApplied,
  rate,
  searchTerm,
  semanticHighlights,
  showBriefingHighlights,
  totalMatches,
  videoRef,
  youtubeId,
  onAddHighlight,
  onAddTag,
  onAddToCollection,
  onAnalyzeKpia,
  onBack,
  onChangeRate,
  onClose,
  onCloseActionMenu,
  onCloseSearch,
  onDelete,
  onEdit,
  onListen,
  onNextMatch,
  onOpenExposureMenu,
  onOpenFullscreen,
  onOpenKpia,
  onOpenPerformance,
  onOpenPublicLink,
  onOpenSearch,
  onOpenSentimentMenu,
  onOpenShare,
  onOpenTagDialog,
  onPrevMatch,
  onRecalculateKpia,
  onRemoveHighlight,
  onRemoveTag,
  onSearchChange,
  onStopSpeech,
  onToggleFavorite,
  onToggleHighlight,
  onToggleRead,
  onToggleSpeech,
  readOnly
}) => (
  <Grid container spacing={3}>
    <Grid item xs={12} md={8.52}>
      {isMobile && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            minHeight: 40,
            mb: 1,
            borderBottom: '1px solid',
            borderColor: 'divider'
          }}
        >
          <ButtonBase
            onClick={onBack}
            aria-label="Voltar para a lista de publicações"
            sx={{
              minHeight: 40,
              px: 0.5,
              pr: 1.25,
              gap: 0.75,
              borderRadius: 1.5,
              color: 'text.secondary'
            }}
          >
            <ArrowBackIcon sx={{ fontSize: 19 }} />
            <Typography sx={{ fontSize: '0.78rem', fontWeight: 750 }}>
              Voltar
            </Typography>
          </ButtonBase>
        </Box>
      )}

      <ArticleModalSectionTabs
        active="publication"
        onPerformance={onOpenPerformance}
        onPublication={() => {}}
        onSmartScore={onOpenKpia}
      />

      <ArticleModalHeader
        article={article}
        actionAnchorEl={actionAnchorEl}
        isMobile={isMobile}
        kpiaAnalyzing={kpiaAnalyzing}
        qualitativeAnalysisApplied={qualitativeAnalysisApplied}
        readOnly={readOnly}
        showKpia={!readOnly}
        onAddToCollection={onAddToCollection}
        onAnalyzeKpia={onAnalyzeKpia}
        onBack={onBack}
        onClose={onClose}
        onCloseActionMenu={onCloseActionMenu}
        onDelete={onDelete}
        onEdit={onEdit}
        onOpenExposureMenu={onOpenExposureMenu}
        onOpenPublicLink={onOpenPublicLink}
        onOpenSentimentMenu={onOpenSentimentMenu}
        onOpenShare={onOpenShare}
        onOpenTagDialog={onOpenTagDialog}
        onToggleFavorite={onToggleFavorite}
        onToggleRead={onToggleRead}
      />

      <ArticleModalReadingTools
        compact
        highlightEnabled={highlightEnabled}
        isPaused={isPaused}
        isSearchOpen={isSearchOpen}
        isSpeaking={isSpeaking}
        isTTSSupported={isTTSSupported}
        onListen={onListen}
        onOpenSearch={onOpenSearch}
        onToggleHighlight={onToggleHighlight}
        showHighlight={!readOnly}
      />

      <ArticleModalActiveBars
        currentMatch={currentMatch}
        isPaused={isPaused}
        isSearchOpen={isSearchOpen}
        isSpeaking={isSpeaking}
        progress={progress}
        rate={rate}
        searchTerm={searchTerm}
        totalMatches={totalMatches}
        onChangeRate={onChangeRate}
        onCloseSearch={onCloseSearch}
        onNextMatch={onNextMatch}
        onPrevMatch={onPrevMatch}
        onSearchChange={onSearchChange}
        onStopSpeech={onStopSpeech}
        onToggleSpeech={onToggleSpeech}
      />

      <TextHighlighter
        containerRef={contentContainerRef}
        highlights={highlights}
        onAddHighlight={onAddHighlight}
        onRemoveHighlight={onRemoveHighlight}
        articleId={article?._id}
        isEnabled={highlightEnabled}
        briefingKeywords={briefingKeywords}
        showBriefingHighlights={showBriefingHighlights}
        semanticHighlights={semanticHighlights}
      />

      <Box ref={contentContainerRef}>
        {article.mediaType === 'LinkedIn' ? (
          <ArticleModalLinkedInContent
            article={article}
            fontSize={fontSize}
            isOpen={isOpen}
            onAddTag={onAddTag}
            onRemoveTag={onRemoveTag}
          />
        ) : (
          <ArticleModalStandardContent
            article={article}
            fontSize={fontSize}
            isOpen={isOpen}
            videoRef={videoRef}
            youtubeId={youtubeId}
            onAddTag={onAddTag}
            onOpenFullscreen={onOpenFullscreen}
            onRemoveTag={onRemoveTag}
            readOnly={readOnly}
          />
        )}
      </Box>
    </Grid>

    <ArticleModalSidebar
      article={article}
      isMobile={isMobile}
      onOpenKpia={onOpenKpia}
      onRecalculate={onRecalculateKpia}
      readOnly={readOnly}
    />
  </Grid>
);

ArticleModalLoadedContent.propTypes = {
  actionAnchorEl: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  article: PropTypes.shape({
    _id: PropTypes.string,
    mediaType: PropTypes.string
  }).isRequired,
  briefingKeywords: PropTypes.array,
  contentContainerRef: PropTypes.shape({ current: PropTypes.any }).isRequired,
  currentMatch: PropTypes.number,
  fontSize: PropTypes.string,
  highlightEnabled: PropTypes.bool.isRequired,
  highlights: PropTypes.array,
  isMobile: PropTypes.bool.isRequired,
  isOpen: PropTypes.bool.isRequired,
  isPaused: PropTypes.bool.isRequired,
  isSearchOpen: PropTypes.bool.isRequired,
  isSpeaking: PropTypes.bool.isRequired,
  isTTSSupported: PropTypes.bool.isRequired,
  kpiaAnalyzing: PropTypes.bool.isRequired,
  progress: PropTypes.number,
  qualitativeAnalysisApplied: PropTypes.bool.isRequired,
  rate: PropTypes.number,
  searchTerm: PropTypes.string,
  semanticHighlights: PropTypes.array,
  showBriefingHighlights: PropTypes.bool.isRequired,
  totalMatches: PropTypes.number,
  videoRef: PropTypes.shape({ current: PropTypes.any }).isRequired,
  youtubeId: PropTypes.string,
  onAddHighlight: PropTypes.func.isRequired,
  onAddTag: PropTypes.func.isRequired,
  onAddToCollection: PropTypes.func.isRequired,
  onAnalyzeKpia: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  onChangeRate: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onCloseActionMenu: PropTypes.func.isRequired,
  onCloseSearch: PropTypes.func.isRequired,
  onDelete: PropTypes.func,
  onEdit: PropTypes.func,
  onListen: PropTypes.func.isRequired,
  onNextMatch: PropTypes.func.isRequired,
  onOpenExposureMenu: PropTypes.func.isRequired,
  onOpenFullscreen: PropTypes.func.isRequired,
  onOpenKpia: PropTypes.func.isRequired,
  onOpenPerformance: PropTypes.func.isRequired,
  onOpenPublicLink: PropTypes.func.isRequired,
  onOpenSearch: PropTypes.func.isRequired,
  onOpenSentimentMenu: PropTypes.func.isRequired,
  onOpenShare: PropTypes.func.isRequired,
  onOpenTagDialog: PropTypes.func.isRequired,
  onPrevMatch: PropTypes.func.isRequired,
  onRecalculateKpia: PropTypes.func.isRequired,
  onRemoveHighlight: PropTypes.func.isRequired,
  onRemoveTag: PropTypes.func,
  onSearchChange: PropTypes.func.isRequired,
  onStopSpeech: PropTypes.func.isRequired,
  onToggleFavorite: PropTypes.func.isRequired,
  onToggleHighlight: PropTypes.func.isRequired,
  onToggleRead: PropTypes.func.isRequired,
  onToggleSpeech: PropTypes.func.isRequired,
  readOnly: PropTypes.bool
};

ArticleModalLoadedContent.defaultProps = {
  actionAnchorEl: null,
  briefingKeywords: [],
  currentMatch: 0,
  fontSize: undefined,
  highlights: [],
  onDelete: null,
  onEdit: null,
  onRemoveTag: null,
  progress: 0,
  rate: 1,
  searchTerm: '',
  semanticHighlights: [],
  totalMatches: 0,
  youtubeId: null,
  readOnly: false
};

export default ArticleModalLoadedContent;
